import { useState } from "react";
import type { AdminPlatform, NewPlatform, PlatformEdit } from "../../api/admin";
import { Badge } from "../../components/admin/AdminUi";
import { DataTable, type Column } from "../../components/admin/DataTable";
import { Banner } from "../../components/Banner";
import { Field, inputShell } from "../../components/Field";
import { safeHref } from "../../lib/format";

interface PlatformsScreenProps {
  platforms: AdminPlatform[];
  busy: boolean;
  message: string | null;
  error: string | null;
  editing: AdminPlatform | "new" | null;
  onEdit: (platform: AdminPlatform | "new" | null) => void;
  onSave: (edit: PlatformEdit) => void;
  onCreate: (platform: NewPlatform) => void;
}

const splitHosts = (value: string) =>
  value
    .split(",")
    .map((host) => host.trim())
    .filter(Boolean);

const isPlaceholder = (url: string) => /\.example\b/.test(url);

/** Partner platforms as a table; editing happens in a side drawer so the list stays readable. */
export function PlatformsScreen(props: PlatformsScreenProps) {
  const { platforms, editing } = props;
  const placeholders = platforms.filter((p) => isPlaceholder(p.referralUrl)).length;

  const columns: Column<AdminPlatform>[] = [
    {
      key: "name",
      header: "Platform",
      sortValue: (p) => p.name.toLowerCase(),
      render: (p) => (
        <div className="flex items-center gap-3">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-[8px] bg-accent text-[13px] font-bold text-on-accent">
            {p.letter}
          </span>
          <div className="min-w-0">
            <div className="font-semibold">{p.name}</div>
            <div className="truncate text-[12px] text-text-muted">{p.description}</div>
          </div>
        </div>
      ),
    },
    {
      key: "link",
      header: "Referral link",
      hideBelow: "md",
      render: (p) =>
        isPlaceholder(p.referralUrl) ? (
          <Badge tone="warning">Placeholder: replace before launch</Badge>
        ) : (
          <a
            href={safeHref(p.referralUrl)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="block max-w-[320px] truncate text-accent underline"
          >
            {p.referralUrl}
          </a>
        ),
    },
    {
      key: "hosts",
      header: "Profile hosts",
      hideBelow: "lg",
      render: (p) => (
        <span className="text-[12.5px] text-text-muted">{p.hosts.join(", ") || "–"}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortValue: (p) => (p.active ? 0 : 1),
      render: (p) => (p.active ? <Badge tone="positive">Shown</Badge> : <Badge>Hidden</Badge>),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {placeholders ? (
        <Banner tone="warn">
          {placeholders} referral link{placeholders === 1 ? " is" : "s are"} still a placeholder
          from the mock. Participants can't sign up through {placeholders === 1 ? "it" : "them"}{" "}
          until you replace {placeholders === 1 ? "it" : "them"}.
        </Banner>
      ) : null}
      {props.message ? <Banner tone="good">{props.message}</Banner> : null}
      <DataTable
        rows={platforms}
        columns={columns}
        rowKey={(p) => p.id}
        onRowClick={(p) => props.onEdit(p)}
        selectedKey={editing && editing !== "new" ? editing.id : null}
        rowLabel={(p) => `Edit ${p.name}`}
        empty="No platforms yet. Run npm run seed:platforms, or add one."
      />
      {editing ? <PlatformDrawer {...props} editing={editing} /> : null}
    </div>
  );
}

function PlatformDrawer({
  editing,
  busy,
  error,
  onEdit,
  onSave,
  onCreate,
}: PlatformsScreenProps & { editing: AdminPlatform | "new" }) {
  const isNew = editing === "new";
  const [draft, setDraft] = useState({
    key: isNew ? "" : editing.key,
    name: isNew ? "" : editing.name,
    description: isNew ? "" : editing.description,
    referralUrl: isNew ? "" : editing.referralUrl,
    hosts: isNew ? "" : editing.hosts.join(", "),
    active: isNew ? true : editing.active,
  });
  const set = (patch: Partial<typeof draft>) => setDraft({ ...draft, ...patch });
  const field = (
    key: "name" | "description" | "referralUrl" | "hosts",
    label: string,
    hint?: string,
  ) => (
    <Field label={label} htmlFor={`pf-${key}`} hint={hint}>
      <input
        id={`pf-${key}`}
        className={inputShell}
        value={draft[key]}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </Field>
  );

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#231416]/30"
        aria-hidden="true"
        onClick={() => onEdit(null)}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={isNew ? "Add a platform" : `Edit ${editing.name}`}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[460px] flex-col bg-surface shadow-[-12px_0_40px_rgba(35,20,22,0.18)]"
      >
        <div className="flex items-center justify-between border-b border-border p-5">
          <b className="text-[17px]">{isNew ? "Add a platform" : `Edit ${editing.name}`}</b>
          <button
            type="button"
            aria-label="Close"
            onClick={() => onEdit(null)}
            className="h-9 w-9 rounded-[9px] text-text-muted hover:bg-surface-high"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {isNew ? (
            <Field label="Key (lowercase, no spaces)" htmlFor="pf-key">
              <input
                id="pf-key"
                className={inputShell}
                value={draft.key}
                onChange={(e) => set({ key: e.target.value })}
              />
            </Field>
          ) : null}
          {field("name", "Name")}
          {field("description", "Description")}
          {field("referralUrl", "Referral sign-up link")}
          {field(
            "hosts",
            "Profile hosts (comma separated)",
            "A profile link only verifies for this platform if its host matches one of these.",
          )}
          <label className="mt-1 flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={draft.active}
              onChange={() => set({ active: !draft.active })}
            />
            Show this platform to participants
          </label>
          {error ? <Banner tone="bad">{error}</Banner> : null}
        </div>
        <div className="flex gap-2 border-t border-border p-4">
          <button
            type="button"
            disabled={
              busy ||
              !draft.name.trim() ||
              !draft.referralUrl.trim() ||
              (isNew && !draft.key.trim())
            }
            onClick={() =>
              isNew
                ? onCreate({
                    key: draft.key,
                    name: draft.name,
                    description: draft.description,
                    referralUrl: draft.referralUrl,
                    hosts: splitHosts(draft.hosts),
                  })
                : onSave({
                    id: editing.id,
                    name: draft.name,
                    description: draft.description,
                    referralUrl: draft.referralUrl,
                    hosts: splitHosts(draft.hosts),
                    active: draft.active,
                  })
            }
            className="h-10 flex-1 rounded-[10px] bg-accent text-[14px] font-bold text-on-accent disabled:opacity-50"
          >
            {busy ? "Saving..." : isNew ? "Add platform" : "Save changes"}
          </button>
          <button
            type="button"
            onClick={() => onEdit(null)}
            className="h-10 rounded-[10px] border border-border px-4 text-[14px] font-semibold"
          >
            Cancel
          </button>
        </div>
      </aside>
    </>
  );
}
